import { Controller } from "@hotwired/stimulus";

// Module-level cache of <meta> defaults. The values are page-global and only
// change on a full reload, so reading them once per page avoids
// hundreds of document.querySelector calls during SlickGrid virtualisation.
let metaCache = null;
function readMetaCache() {
  if (metaCache) return metaCache;
  const meta = (name) =>
    document.querySelector(`meta[name="${name}"]`)?.content;
  metaCache = {
    numericFontMonospace: meta("numericFontMonospace") === "true",
    userRoundPreference: meta("userRoundPreference") || "units",
    userCommaStyle: meta("userCommaStyle") || "indian",
    userPrecision: meta("userPrecision") || "",
    displayZeroAs: meta("displayZeroAs") || "",
  };
  return metaCache;
}

// Re-using Intl.NumberFormat instances is dramatically faster than
// constructing a fresh formatter on every cell render.
const formatterCache = new Map();
function getFormatter(locale, minFractionDigits) {
  const key = `${locale}|${minFractionDigits}`;
  let f = formatterCache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale, {
      minimumFractionDigits: minFractionDigits,
    });
    formatterCache.set(key, f);
  }
  return f;
}

export default class extends Controller {
  static values = {
    amount: Number,
    commaStyle: { type: String, default: undefined },
    numberScale: { type: String, default: undefined },
    precision: { type: String, default: undefined },
  };

  connect() {
    this.format();
  }

  applyScale(scale) {
    this.numberScaleOverride = scale || "";
    this.format();
  }

  format() {
    if (this.amountValue < 0) {
      this.element.classList.add("text-red-600");
    }
    if (readMetaCache().numericFontMonospace) {
      this.element.classList.add("jetbrains-mono");
    }
    this.element.innerHTML = this.styleAmount(this.amountValue);
  }

  // if any of the below methods are changes pls change in application.js also

  styleAmount(amt) {
    if (parseFloat(amt) === 0) {
      return this.displayZeroAs();
    }
    const formatter = getFormatter(this.userCommaStyle(), this.precision());
    if (parseFloat(amt) < 0) {
      return `(${formatter.format(this.roundAmount(amt))})`;
    } else {
      return formatter.format(this.roundAmount(amt));
    }
  }

  precision() {
    if (this.userPrecision() === "") {
      if (this.userRoundPreference() === "units") {
        return 0;
      } else {
        return 2;
      }
    } else {
      return parseInt(this.userPrecision());
    }
  }

  userRoundPreference() {
    var scale = this.activeScaleOverride() || this.numberScaleValue;
    if (
      scale === "" ||
      scale === "null" ||
      scale === "undefined" ||
      scale === undefined ||
      scale === "undefined"
    ) {
      scale = readMetaCache().userRoundPreference;
    }
    return scale;
  }

  activeScaleOverride() {
    return this.numberScaleOverride || this.reportScaleOverride();
  }

  reportScaleOverride() {
    const host = this.element.closest("[data-report-scale]");
    const scale = host && host.getAttribute("data-report-scale");
    return scale && scale !== "" && scale !== "default" ? scale : null;
  }

  userCommaStyle() {
    var style = this.commaStyleValue;
    if (
      style === "" ||
      style === "null" ||
      style === "undefined" ||
      style === undefined ||
      style === "undefined"
    ) {
      style = readMetaCache().userCommaStyle;
    }
    if (style === "western") {
      return "en-US";
    } else {
      return "en-IN";
    }
  }

  userPrecision() {
    var roundoff = this.activeScaleOverride() ? "" : this.precisionValue;
    if (
      roundoff === "" ||
      roundoff === "null" ||
      roundoff === undefined ||
      roundoff === "undefined"
    ) {
      roundoff = readMetaCache().userPrecision;
    }
    return roundoff;
  }

  roundAmount(amt) {
    var preference = this.userRoundPreference();
    var rounding = {
      units: 1,
      thousands: 1000,
      lakhs: 100000,
      millions: 1000000,
      crores: 10000000,
    };
    return parseFloat(
      (Math.abs(amt) / rounding[preference]).toFixed(this.precision()),
    );
  }

  displayZeroAs() {
    return readMetaCache().displayZeroAs === "zero" ? "0" : "-";
  }
}
